import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API_BASE } from "../components/AuthContext";
import { toast } from "sonner";
import { Plus, Check, Trash2, CalendarClock, Flag } from "lucide-react";
import { logError } from "../lib/log";
import { TaskAssignControl, TaskSourceChip } from "../components/CreateTaskDialog";

const PRIORITY_STYLES = {
  high: { color: "#B91C1C", label: "High" },
  medium: { color: "#5E6B82", label: "Medium" },
  low: { color: "#5E6B82", label: "Low" },
};

const DUE_GROUPS = [
  { key: "overdue", label: "Overdue" },
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "later", label: "Later" },
  { key: "no_due", label: "No due date" },
];

function formatDueAt(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return s; }
}

function groupTask(t) {
  if (t.status === "done") return null;
  if (!t.due_at) return "no_due";
  const now = new Date();
  const due = new Date(t.due_at);
  if (Number.isNaN(due.getTime())) return "no_due";
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  const weekEnd = new Date(today); weekEnd.setDate(today.getDate() + 7);
  if (due < now && due < today) return "overdue";
  if (due >= today && due < tomorrow) return "today";
  if (due >= tomorrow && due <= weekEnd) return "week";
  return "later";
}

function TaskRow({ task, onComplete, onDelete, onReopen, selected, onToggleSelect, onChanged }) {
  const prio = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium;
  return (
    <div
      data-testid={`task-row-${task.task_id}`}
      className="flex items-start gap-3 px-3 py-2.5 border-b last:border-b-0"
      style={{ borderColor: "var(--border)", background: selected ? "var(--subtle)" : "transparent" }}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={() => onToggleSelect(task.task_id)}
        className="mt-1.5 w-3.5 h-3.5"
        data-testid={`task-select-${task.task_id}`}
        title="Select for bulk action"
      />
      {task.status === "open" ? (
        <button
          type="button"
          onClick={() => onComplete(task)}
          data-testid={`task-complete-${task.task_id}`}
          className="mt-0.5 w-5 h-5 rounded border flex items-center justify-center hover:bg-[var(--subtle)]"
          style={{ borderColor: "var(--border)" }}
          title="Mark done"
        >
          <Check size={12} className="opacity-0 hover:opacity-100" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => onReopen(task)}
          data-testid={`task-reopen-${task.task_id}`}
          className="mt-0.5 w-5 h-5 rounded flex items-center justify-center"
          style={{ background: "var(--ink)", color: "var(--paper)" }}
          title="Reopen"
        >
          <Check size={12} />
        </button>
      )}
      <div className="flex-1 min-w-0">
        <div
          className={`text-[14px] font-medium ${task.status === "done" ? "line-through text-[var(--ink-3)]" : "text-[var(--ink)]"}`}
          data-testid={`task-title-${task.task_id}`}
        >
          {task.title}
        </div>
        {task.description && (
          <div className="text-[12px] text-[var(--ink-3)] mt-0.5 line-clamp-2">{task.description}</div>
        )}
        <div className="flex items-center gap-3 flex-wrap mt-1 text-[11px] text-[var(--ink-3)]">
          {task.due_at && (
            <span className="inline-flex items-center gap-1">
              <CalendarClock size={11} /> {formatDueAt(task.due_at)}
            </span>
          )}
          <TaskAssignControl task={task} onChanged={onChanged} testid={`task-${task.task_id}`} />
          <span className="inline-flex items-center gap-1" style={{ color: prio.color }}>
            <Flag size={11} /> {prio.label}
          </span>
          {task.parent_kind && task.parent_kind !== "none" && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: "var(--subtle)" }}>
              {task.parent_kind}
            </span>
          )}
          <TaskSourceChip task={task} />
        </div>
      </div>
      <button
        type="button"
        onClick={() => onDelete(task)}
        data-testid={`task-delete-${task.task_id}`}
        className="text-[var(--ink-3)] hover:text-[var(--accent)] p-1"
        title="Delete"
      >
        <Trash2 size={13} />
      </button>
    </div>
  );
}

function NewTaskForm({ onCreate, members, defaultAssigneeId }) {
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [assigneeId, setAssigneeId] = useState(defaultAssigneeId || "");
  const [priority, setPriority] = useState("medium");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    setBusy(true);
    try {
      await onCreate({
        title: title.trim(),
        due_at: dueAt ? new Date(dueAt).toISOString() : null,
        assignee_id: assigneeId || null,
        priority,
        parent_kind: "none",
      });
      setTitle(""); setDueAt("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="tasks-new-form"
      className="flex items-center gap-2 px-3 py-3 border rounded-lg"
      style={{ borderColor: "var(--border)", background: "var(--paper)" }}
    >
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Add a task or follow-up…"
        data-testid="tasks-new-title"
        className="flex-1 px-3 py-2 rounded-md border bg-transparent text-[14px]"
        style={{ borderColor: "var(--border)" }}
      />
      <input
        type="datetime-local"
        value={dueAt}
        onChange={(e) => setDueAt(e.target.value)}
        data-testid="tasks-new-due"
        className="px-2 py-2 rounded-md border bg-transparent text-[12px]"
        style={{ borderColor: "var(--border)" }}
      />
      <select
        value={assigneeId}
        onChange={(e) => setAssigneeId(e.target.value)}
        data-testid="tasks-new-assignee"
        className="px-2 py-2 rounded-md border bg-transparent text-[12px]"
        style={{ borderColor: "var(--border)" }}
      >
        <option value="">Unassigned</option>
        {members.map((m) => (<option key={m.user_id} value={m.user_id}>{m.name}</option>))}
      </select>
      <select
        value={priority}
        onChange={(e) => setPriority(e.target.value)}
        data-testid="tasks-new-priority"
        className="px-2 py-2 rounded-md border bg-transparent text-[12px]"
        style={{ borderColor: "var(--border)" }}
      >
        <option value="low">Low</option>
        <option value="medium">Medium</option>
        <option value="high">High</option>
      </select>
      <button
        type="submit"
        disabled={busy}
        data-testid="tasks-new-submit"
        className="px-3 py-2 rounded-md text-[13px] font-medium disabled:opacity-50"
        style={{ background: "var(--ink)", color: "var(--paper)" }}
      >
        <Plus size={14} className="inline mr-1" /> Add
      </button>
    </form>
  );
}

export default function Tasks() {
  const { state } = useAuth();
  const myId = state?.user?.user_id;
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const [scope, setScope] = useState("mine");   // mine | all
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAssigneeOpen, setBulkAssigneeOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [filterAssignees, setFilterAssignees] = useState(new Set());
  const [filterPriorities, setFilterPriorities] = useState(new Set());

  const toggleAssigneeFilter = (uid) => {
    setFilterAssignees((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };
  const togglePriorityFilter = (p) => {
    setFilterPriorities((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p); else next.add(p);
      return next;
    });
  };
  const clearFilters = () => { setFilterAssignees(new Set()); setFilterPriorities(new Set()); };

  const toggleSelect = useCallback((tid) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(tid)) next.delete(tid); else next.add(tid);
      return next;
    });
  }, []);
  const clearSelection = () => setSelectedIds(new Set());

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (scope === "mine") params.set("assignee_id", "me");
    try {
      const r = await axios.get(`${API_BASE}/tasks?${params.toString()}`);
      setTasks(r.data || []);
      // Drop selection of tasks that no longer exist
      const live = new Set((r.data || []).map((t) => t.task_id));
      setSelectedIds((prev) => new Set([...prev].filter((id) => live.has(id))));
    } catch (e) {
      logError("tasks:list", e);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  const bulkAction = async (action, extra = {}) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    if (action === "delete" && !window.confirm(`Delete ${ids.length} task(s)?`)) return;
    setBulkBusy(true);
    try {
      const r = await axios.post(`${API_BASE}/tasks/bulk`, {
        action, task_ids: ids, ...extra,
      });
      toast.success(`${r.data.affected} task(s) ${action === "delete" ? "deleted" : action === "complete" ? "marked done" : action === "reopen" ? "reopened" : "reassigned"}`);
      clearSelection();
      setBulkAssigneeOpen(false);
      await fetchTasks();
      window.dispatchEvent(new Event("tasks:refresh-pending"));
    } catch (e) {
      logError("tasks:bulk", e);
      toast.error(e?.response?.data?.detail || `Bulk ${action} failed`);
    } finally { setBulkBusy(false); }
  };

  useEffect(() => {
    let cancel = false;
    axios.get(`${API_BASE}/team/members`).then((r) => { if (!cancel) setMembers(r.data || []); }).catch((e) => logError("tasks:members", e));
    return () => { cancel = true; };
  }, []);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  const onCreate = async (body) => {
    try {
      await axios.post(`${API_BASE}/tasks`, body);
      toast.success("Task added");
      await fetchTasks();
      window.dispatchEvent(new Event("tasks:refresh-pending"));
    } catch (e) {
      logError("tasks:create", e);
      toast.error(e?.response?.data?.detail || "Failed to add task");
    }
  };

  const onComplete = async (t) => {
    try {
      await axios.post(`${API_BASE}/tasks/${t.task_id}/complete`);
      await fetchTasks();
      window.dispatchEvent(new Event("tasks:refresh-pending"));
    } catch (e) {
      logError("tasks:complete", e);
      toast.error("Could not complete task");
    }
  };

  const onReopen = async (t) => {
    try {
      await axios.post(`${API_BASE}/tasks/${t.task_id}/reopen`);
      await fetchTasks();
    } catch (e) {
      logError("tasks:reopen", e);
    }
  };

  const onDelete = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await axios.delete(`${API_BASE}/tasks/${t.task_id}`);
      await fetchTasks();
      window.dispatchEvent(new Event("tasks:refresh-pending"));
    } catch (e) {
      logError("tasks:delete", e);
      toast.error("Delete failed");
    }
  };

  const grouped = useMemo(() => {
    const buckets = { overdue: [], today: [], week: [], later: [], no_due: [], done: [] };
    for (const t of tasks) {
      if (filterAssignees.size > 0 && !filterAssignees.has(t.assignee_id || "__unassigned__")) continue;
      if (filterPriorities.size > 0 && !filterPriorities.has(t.priority || "medium")) continue;
      if (t.status === "done") {
        buckets.done.push(t);
        continue;
      }
      const g = groupTask(t);
      if (g) buckets[g].push(t);
    }
    for (const k of Object.keys(buckets)) {
      buckets[k].sort((a, b) => {
        if (!a.due_at && !b.due_at) return 0;
        if (!a.due_at) return 1;
        if (!b.due_at) return -1;
        return a.due_at.localeCompare(b.due_at);
      });
    }
    return buckets;
  }, [tasks, filterAssignees, filterPriorities]);

  return (
    <div className="p-8 max-w-4xl mx-auto" data-testid="tasks-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tight text-[var(--ink)]">Tasks</h1>
          <p className="text-[var(--ink-2)] text-sm mt-1">Follow-ups, callbacks, and ad-hoc to-dos.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setScope(scope === "mine" ? "all" : "mine")}
            data-testid="tasks-scope-toggle"
            className="px-3 py-1.5 rounded-full text-[12px] font-medium border"
            style={{
              borderColor: "var(--border)",
              background: scope === "mine" ? "var(--ink)" : "transparent",
              color: scope === "mine" ? "var(--paper)" : "var(--ink-2)",
            }}
          >
            {scope === "mine" ? "My tasks" : "All tasks"}
          </button>
          <button
            type="button"
            onClick={() => setShowDone((v) => !v)}
            data-testid="tasks-show-done"
            className="px-3 py-1.5 rounded-full text-[12px] font-medium border"
            style={{
              borderColor: "var(--border)",
              background: showDone ? "var(--ink)" : "transparent",
              color: showDone ? "var(--paper)" : "var(--ink-2)",
            }}
          >
            {showDone ? "Hide done" : "Show done"}
          </button>
        </div>
      </div>

      <NewTaskForm onCreate={onCreate} members={members} defaultAssigneeId={myId} />

      <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="tasks-filter-chips">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] uppercase tracking-wide text-[var(--ink-3)] mr-1">Priority</span>
          {["high", "medium", "low"].map((p) => {
            const on = filterPriorities.has(p);
            return (
              <button
                key={p}
                type="button"
                onClick={() => togglePriorityFilter(p)}
                data-testid={`tasks-filter-priority-${p}`}
                className="px-2.5 py-1 rounded-full text-[11px] font-medium border transition"
                style={{
                  borderColor: on ? "var(--ink)" : "var(--border)",
                  background: on ? "var(--ink)" : "transparent",
                  color: on ? "var(--paper)" : "var(--ink-2)",
                }}
              >
                {p[0].toUpperCase() + p.slice(1)}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] uppercase tracking-wide text-[var(--ink-3)] mr-1">Assignee</span>
          <button
            type="button"
            onClick={() => toggleAssigneeFilter("__unassigned__")}
            data-testid="tasks-filter-assignee-unassigned"
            className="px-2.5 py-1 rounded-full text-[11px] font-medium border transition"
            style={{
              borderColor: filterAssignees.has("__unassigned__") ? "var(--ink)" : "var(--border)",
              background: filterAssignees.has("__unassigned__") ? "var(--ink)" : "transparent",
              color: filterAssignees.has("__unassigned__") ? "var(--paper)" : "var(--ink-2)",
            }}
          >Unassigned</button>
          {members.map((m) => {
            const on = filterAssignees.has(m.user_id);
            return (
              <button
                key={m.user_id}
                type="button"
                onClick={() => toggleAssigneeFilter(m.user_id)}
                data-testid={`tasks-filter-assignee-${m.user_id}`}
                className="px-2.5 py-1 rounded-full text-[11px] font-medium border transition"
                style={{
                  borderColor: on ? "var(--ink)" : "var(--border)",
                  background: on ? "var(--ink)" : "transparent",
                  color: on ? "var(--paper)" : "var(--ink-2)",
                }}
              >
                {m.user_id === myId ? "Me" : m.name?.split(" ")[0] || m.email}
              </button>
            );
          })}
        </div>
        {(filterAssignees.size > 0 || filterPriorities.size > 0) && (
          <button
            type="button"
            onClick={clearFilters}
            data-testid="tasks-filter-clear"
            className="text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)] underline ml-auto"
          >Clear filters</button>
        )}
      </div>

      {selectedIds.size > 0 && (
        <div
          className="mt-4 flex items-center gap-2 px-3 py-2 rounded-md border"
          style={{ borderColor: "var(--ink)", background: "var(--subtle)" }}
          data-testid="tasks-bulk-toolbar"
        >
          <span className="text-[13px] font-medium text-[var(--ink)]" data-testid="tasks-bulk-count">
            {selectedIds.size} selected
          </span>
          <button
            type="button"
            onClick={() => bulkAction("complete")}
            disabled={bulkBusy}
            className="px-3 py-1 rounded text-[12px] font-medium disabled:opacity-50"
            style={{ background: "var(--ink)", color: "var(--paper)" }}
            data-testid="tasks-bulk-complete"
          >Mark done</button>
          <button
            type="button"
            onClick={() => bulkAction("reopen")}
            disabled={bulkBusy}
            className="px-3 py-1 rounded text-[12px] font-medium border disabled:opacity-50"
            style={{ borderColor: "var(--border)", color: "var(--ink-2)" }}
            data-testid="tasks-bulk-reopen"
          >Reopen</button>
          <div className="relative">
            <button
              type="button"
              onClick={() => setBulkAssigneeOpen((v) => !v)}
              disabled={bulkBusy}
              className="px-3 py-1 rounded text-[12px] font-medium border disabled:opacity-50"
              style={{ borderColor: "var(--border)", color: "var(--ink-2)" }}
              data-testid="tasks-bulk-reassign-open"
            >Reassign…</button>
            {bulkAssigneeOpen && (
              <div
                className="absolute left-0 top-full mt-1 max-h-64 overflow-y-auto rounded-md border shadow-md z-10 w-56"
                style={{ background: "var(--paper)", borderColor: "var(--border)" }}
                data-testid="tasks-bulk-reassign-menu"
              >
                <button
                  type="button"
                  onClick={() => bulkAction("reassign", { assignee_id: null })}
                  className="w-full text-left px-3 py-2 text-[12px] hover:bg-[var(--subtle)] border-b"
                  style={{ borderColor: "var(--border)" }}
                  data-testid="tasks-bulk-reassign-unassign"
                >— Unassign —</button>
                {members.map((m) => (
                  <button
                    key={m.user_id}
                    type="button"
                    onClick={() => bulkAction("reassign", { assignee_id: m.user_id })}
                    className="w-full text-left px-3 py-2 text-[12px] hover:bg-[var(--subtle)]"
                    data-testid={`tasks-bulk-reassign-${m.user_id}`}
                  >{m.name}</button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => bulkAction("delete")}
            disabled={bulkBusy}
            className="px-3 py-1 rounded text-[12px] font-medium border disabled:opacity-50"
            style={{ borderColor: "var(--border)", color: "var(--accent)" }}
            data-testid="tasks-bulk-delete"
          >Delete</button>
          <button
            type="button"
            onClick={clearSelection}
            className="ml-auto px-2 py-1 text-[11px] text-[var(--ink-3)] hover:text-[var(--ink)]"
            data-testid="tasks-bulk-clear"
          >Clear</button>
        </div>
      )}

      {loading ? (
        <div className="text-[var(--ink-3)] text-sm mt-6" data-testid="tasks-loading">Loading…</div>
      ) : (
        <div className="mt-6 space-y-6" data-testid="tasks-list">
          {DUE_GROUPS.map((g) => grouped[g.key].length > 0 && (
            <section key={g.key} data-testid={`tasks-group-${g.key}`}>
              <div className="text-[11px] uppercase tracking-wide text-[var(--ink-3)] mb-2 px-1">
                {g.label} <span className="ml-1 font-mono">{grouped[g.key].length}</span>
              </div>
              <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--border)" }}>
                {grouped[g.key].map((t) => (
                  <TaskRow key={t.task_id} task={t} onComplete={onComplete} onDelete={onDelete} onReopen={onReopen} selected={selectedIds.has(t.task_id)} onToggleSelect={toggleSelect} onChanged={fetchTasks} />
                ))}
              </div>
            </section>
          ))}
          {showDone && grouped.done.length > 0 && (
            <section data-testid="tasks-group-done">
              <div className="text-[11px] uppercase tracking-wide text-[var(--ink-3)] mb-2 px-1">
                Done <span className="ml-1 font-mono">{grouped.done.length}</span>
              </div>
              <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--border)" }}>
                {grouped.done.map((t) => (
                  <TaskRow key={t.task_id} task={t} onComplete={onComplete} onDelete={onDelete} onReopen={onReopen} selected={selectedIds.has(t.task_id)} onToggleSelect={toggleSelect} onChanged={fetchTasks} />
                ))}
              </div>
            </section>
          )}
          {Object.values(grouped).every((arr) => arr.length === 0) && (
            <div className="text-[var(--ink-3)] text-sm mt-4">No tasks yet. Add your first one above.</div>
          )}
        </div>
      )}
    </div>
  );
}
